import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ShieldAlert, CheckCircle2, Loader2, ArrowRight, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useSettings } from "@/context/SettingsContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES = [
  "Individual return", "Self-employed / 1099", "Business return",
  "Bookkeeping", "Tax planning", "Notice assistance", "AI automation", "Other",
];
const INCOME_TYPES = [
  "W-2", "Self-employed / 1099", "Business", "Rental", "Investments", "Multiple states", "Other",
];
const APPT_TYPES = ["Virtual", "Phone", "In-person (if available)"];
const HEARD = ["Google", "Referral", "Social media", "Returning client", "Other"];

const getUTM = () => {
  const p = new URLSearchParams(window.location.search);
  const utm = {};
  ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"].forEach((k) => {
    if (p.get(k)) utm[k] = p.get(k);
  });
  return utm;
};

export default function StartReturn() {
  const settings = useSettings();
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", client_type: "", service: "",
    income_type: "", appointment_type: "", best_time: "", heard: "",
    description: "", consent: false, privacy: false,
  });

  const up = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.service) {
      toast.error("Please complete the required fields.");
      return;
    }
    if (!form.consent || !form.privacy) {
      toast.error("Please provide consent and agree to the Privacy Policy.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        form_type: "start-return",
        name: form.name,
        email: form.email,
        phone: form.phone,
        service_category: form.service,
        lead_source: form.heard,
        source_page: "/start",
        utm: getUTM(),
        consent: form.consent,
        payload: {
          client_type: form.client_type,
          income_type: form.income_type,
          appointment_type: form.appointment_type,
          best_time: form.best_time,
          description: form.description,
        },
      });
      setDone(true);
      window.scrollTo({ top: 0, behavior: "instant" });
    } catch {
      toast.error("Something went wrong. Please try again or call 201-253-7674.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div data-testid="start-return-success">
        <section className="bg-navy text-white noise-overlay py-24 md:py-32">
          <div className="max-w-2xl mx-auto px-4 text-center">
            <CheckCircle2 className="w-16 h-16 text-bronze mx-auto mb-6" />
            <h1 className="font-heading text-4xl sm:text-5xl font-bold mb-4">Request Received!</h1>
            <p className="font-body text-lg text-gray-300 mb-8">
              Thanks, {form.name.split(" ")[0]}. We've received your details and will reach out shortly with your next steps. A confirmation email is on its way.
            </p>
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6 text-left mb-8">
              <p className="font-body text-gray-200 mb-2"><strong className="text-bronze">Next:</strong> Book a consultation so we can get you started faster.</p>
              <p className="font-body text-sm text-gray-400">Tax documents are only uploaded through the secure client portal (MyTAXPrepOffice) after you receive instructions — never by email or web form.</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button onClick={() => window.dispatchEvent(new CustomEvent("open-booking"))} className="bg-bronze hover:bg-bronze/90 text-navy rounded-full px-8 py-6 text-lg font-medium" data-testid="success-book-btn">
                Book a Consultation
              </Button>
              <Button asChild variant="outline" className="border-white text-white hover:bg-white hover:text-navy rounded-full px-8 py-6 text-lg font-medium bg-transparent">
                <Link to="/">Back to Home</Link>
              </Button>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div data-testid="start-return-page">
      <section className="bg-navy text-white noise-overlay py-16 md:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="font-mono text-bronze text-sm mb-4 tracking-wider uppercase">Get Started</p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold mb-4">Start Your Return</h1>
          <p className="font-body text-lg text-gray-300">
            Complete this brief, non-sensitive assessment so we understand your needs. We'll follow up with clear next steps — no tax documents required here.
          </p>
        </div>
      </section>

      <section className="bg-bone py-16 md:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <form onSubmit={submit} className="bg-white rounded-2xl shadow-lg p-6 md:p-10 space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <Label htmlFor="sr-name">Full Name *</Label>
                <Input id="sr-name" data-testid="sr-name" value={form.name} onChange={(e) => up("name", e.target.value)} placeholder="Jane Doe" />
              </div>
              <div>
                <Label htmlFor="sr-phone">Mobile Number *</Label>
                <Input id="sr-phone" data-testid="sr-phone" value={form.phone} onChange={(e) => up("phone", e.target.value)} placeholder="(201) 555-0100" />
              </div>
            </div>
            <div>
              <Label htmlFor="sr-email">Email *</Label>
              <Input id="sr-email" type="email" data-testid="sr-email" value={form.email} onChange={(e) => up("email", e.target.value)} placeholder="you@email.com" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <Label>New or Returning Client?</Label>
                <Select value={form.client_type} onValueChange={(v) => up("client_type", v)}>
                  <SelectTrigger data-testid="sr-client-type"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="New client">New client</SelectItem>
                    <SelectItem value="Returning client">Returning client</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Service Needed *</Label>
                <Select value={form.service} onValueChange={(v) => up("service", v)}>
                  <SelectTrigger data-testid="sr-service"><SelectValue placeholder="Select a service" /></SelectTrigger>
                  <SelectContent>
                    {SERVICES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <Label>General Income Type</Label>
                <Select value={form.income_type} onValueChange={(v) => up("income_type", v)}>
                  <SelectTrigger data-testid="sr-income"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {INCOME_TYPES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Preferred Appointment Type</Label>
                <Select value={form.appointment_type} onValueChange={(v) => up("appointment_type", v)}>
                  <SelectTrigger data-testid="sr-appt"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {APPT_TYPES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <Label htmlFor="sr-time">Best Time to Contact</Label>
                <Input id="sr-time" data-testid="sr-time" value={form.best_time} onChange={(e) => up("best_time", e.target.value)} placeholder="e.g. Weekday mornings" />
              </div>
              <div>
                <Label>How did you hear about us?</Label>
                <Select value={form.heard} onValueChange={(v) => up("heard", v)}>
                  <SelectTrigger data-testid="sr-heard"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {HEARD.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label htmlFor="sr-desc">Brief Description of Your Needs</Label>
              <Textarea id="sr-desc" data-testid="sr-desc" value={form.description} onChange={(e) => up("description", e.target.value)} rows={4} placeholder="Tell us a bit about your situation (no sensitive info, please)." />
            </div>

            <div className="space-y-3">
              <label className="flex items-start gap-3 cursor-pointer">
                <Checkbox checked={form.consent} onCheckedChange={(v) => up("consent", !!v)} data-testid="sr-consent" className="mt-1" />
                <span className="font-body text-sm text-gray-600">I consent to receive service-related emails, calls, and text messages from DA TAXGUY & ASSOCIATES.</span>
              </label>
              <label className="flex items-start gap-3 cursor-pointer">
                <Checkbox checked={form.privacy} onCheckedChange={(v) => up("privacy", !!v)} data-testid="sr-privacy" className="mt-1" />
                <span className="font-body text-sm text-gray-600">I agree to the <Link to="/privacy" className="text-bronze underline">Privacy Policy</Link>.</span>
              </label>
            </div>

            <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3" data-testid="sr-safety-notice">
              <ShieldAlert className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="font-body text-sm text-red-700">
                Please do not enter Social Security numbers, bank-account details, dates of birth, tax documents, photos of identification, or other sensitive information in this form. Accepted clients receive secure MyTAXPrepOffice portal instructions.
              </p>
            </div>

            <Button type="submit" disabled={submitting} className="w-full bg-navy hover:bg-gray-800 text-white rounded-full py-6 text-lg font-medium" data-testid="sr-submit">
              {submitting ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Submitting...</> : <>Submit &amp; Get Next Steps <ArrowRight className="ml-2 w-5 h-5" /></>}
            </Button>
            <p className="text-xs text-gray-500 text-center flex items-center justify-center gap-1"><Lock className="w-3 h-3" /> Your information is submitted securely over HTTPS.</p>
          </form>
        </div>
      </section>
    </div>
  );
}
